import { env } from "@/env";

export interface AccountOrder {
  order_id: string;
  kind: "digital_report" | "physical" | "session" | "subscription";
  title: string;
  purchased_at: string;
  status: string;
  download_url: string | null;
  track_url: string | null;
  amount_inr: number | null;
  // Paid report can be re-sent to the customer's email from the account hub.
  can_resend?: boolean;
  // False = paid report whose copy never reached the buyer (no send/download).
  delivered?: boolean;
  // Receipt-authoritative paid-report lifecycle from the account API.
  delivery_stage?: "paid" | "preparing" | "ready" | "delivered";
  generated_at?: string | null;
  delivered_at?: string | null;
  delivery_channel?: "email" | "whatsapp" | "download" | null;
}

export interface AccountUpsellOffer {
  plan_id: string;
  title: string;
  price_inr: number;
  description: string;
  cta_url: string;
}

export interface AccountMembership {
  active: boolean;
  plan_id: string | null;
  status: string | null;
  provider: string | null;
  current_period_end: string | null;
  subscription_id: string | null;
}

export interface AccountReading {
  reading_id: string;
  title: string;
  created_at: string;
  language: string | null;
  source: string | null;
  chat_url: string;
  assigned_jyotishi: string | null;
  conversation_turns: number;
  last_message: string | null;
  last_message_at: string | null;
  report_type: string | null;
}

export interface AccountJyotishPeriod {
  planet_id: string;
  name_en: string;
  name_hi: string;
}

export interface AccountJyotishTransit {
  planet_id: string;
  name_en: string;
  name_hi: string;
  rashi_en: string;
  rashi_hi: string;
  house_from_moon: number;
  is_retrograde: boolean;
}

export interface AccountJyotishSnapshot {
  reading_id: string;
  as_of: string;
  language: string;
  moon_rashi_en: string;
  moon_rashi_hi: string;
  mahadasha: AccountJyotishPeriod | null;
  antardasha: AccountJyotishPeriod | null;
  transits: AccountJyotishTransit[];
}

export interface AccountOrdersResponse {
  email: string;
  phone?: string;
  contact?: string;
  contact_kind?: "email" | "phone" | "auth";
  orders: AccountOrder[];
  upsells: AccountUpsellOffer[];
  membership: AccountMembership | null;
  readings: AccountReading[];
  jyotishi_snapshot?: AccountJyotishSnapshot | null;
}

export interface CancelMembershipResponse {
  success: boolean;
  membership: AccountMembership & { cancel_at_cycle_end: boolean };
}

export interface ResendOrderResponse {
  ok: boolean;
  deduped: boolean;
  channel: string;
  masked_email: string;
  masked_phone: string;
  masked_destination: string;
  message: string;
}

export async function requestAccountLink(contact: string): Promise<void> {
  const value = contact.trim();
  const body = value.includes("@") ? { email: value } : { phone: value };
  await fetch(`${env.NEXT_PUBLIC_API_URL}/account/request-link`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function accountSessionFromReport(reportToken: string): Promise<string> {
  const res = await fetch(
    `${env.NEXT_PUBLIC_API_URL}/account/from-report/${encodeURIComponent(reportToken)}`,
    { method: "POST" }
  );
  if (!res.ok) throw new Error("This private report link can no longer open the account.");
  const body = (await res.json()) as { token?: string };
  if (!body.token) throw new Error("Account access token was not returned.");
  return body.token;
}

export async function accountSessionFromAuth(
  accessToken: string
): Promise<string | "unauthorized"> {
  const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/account/from-auth`, {
    method: "POST",
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (res.status === 401) return "unauthorized";
  if (!res.ok) throw new Error(`Could not open signed-in account (HTTP ${res.status})`);
  const body = (await res.json()) as { token?: string };
  if (!body.token) throw new Error("Signed-in account token was not returned.");
  return body.token;
}

export async function fetchAccountOrders(
  token: string
): Promise<AccountOrdersResponse | "unauthorized"> {
  const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/account/orders`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) return "unauthorized";
  if (!res.ok) throw new Error(`Failed to load orders (HTTP ${res.status})`);
  return (await res.json()) as AccountOrdersResponse;
}

export async function cancelMembership(token: string): Promise<CancelMembershipResponse> {
  const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/account/membership/cancel`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ cancel_at_cycle_end: true }),
  });
  if (res.status === 401) throw new Error("Your account link has expired. Please sign in again.");
  if (!res.ok) throw new Error(`Failed to cancel membership (HTTP ${res.status})`);
  return (await res.json()) as CancelMembershipResponse;
}

export async function resendOrder(token: string, orderId: string): Promise<ResendOrderResponse> {
  const res = await fetch(
    `${env.NEXT_PUBLIC_API_URL}/account/orders/${encodeURIComponent(orderId)}/resend`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (res.status === 401) throw new Error("Your account link has expired. Please sign in again.");
  if (!res.ok) throw new Error(`Could not send your reading (HTTP ${res.status})`);
  return (await res.json()) as ResendOrderResponse;
}
